import { useCallback, useEffect, useRef, useState } from 'react';
import type { AiDifyCapability } from '../types/aiDifyCapability';
import { PATTERN_FEATURE_MAP } from '../types/aiDifyCapability';
import type { PatternFeatureKey } from '../types/aiDifyCapability';
import type { AiConversationRecord } from '../types/aiHistory';
import { backendChatService } from '../services/backendChatService';
import { conversationHistoryService } from '../services/conversationHistoryService';
import type { UseStreamingMessageReturn } from '../hooks/useStreamingMessage';
import { formatSkillsParam } from '../types/skillAsset';
import type { ChatMessage, ViewMode } from '../types/aiChat';
import { createId, nowIso, mapDbMessagesToUi } from '../types/aiChat';

// ─── 参数 ─────────────────────

export interface UseConversationManagerParams {
  readonly businessType: string;
  readonly businessId: string | number | null;
  readonly currentCapability: AiDifyCapability | null;
  readonly currentCapabilityId: number | undefined;
  readonly skillsEnabled: boolean;
  readonly selectedSkillIds: readonly string[];
  readonly selectedPattern: PatternFeatureKey | null;
  readonly streaming: UseStreamingMessageReturn;
  readonly streamingMsgIdRef: React.MutableRefObject<string | null>;
  readonly scrollToBottom: (force?: boolean) => void;
  readonly onMessageReceived?: (message: string, metadata?: import('../pages/AiChatPage/types').MessageReceivedMetadata) => void;
  readonly onBeforeSend?: (text: string) => string;
  readonly previewLastHtmlMessage: (messages: readonly ChatMessage[]) => void;
  /** 额外输入参数，会合并到 Dify API 的 extra.inputs 中 */
  readonly extraInputs?: Readonly<Record<string, unknown>>;
}

// ─── 返回值 ─────────────────────

export interface UseConversationManagerReturn {
  readonly viewMode: ViewMode;
  readonly setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  readonly messages: readonly ChatMessage[];
  readonly setMessages: React.Dispatch<React.SetStateAction<readonly ChatMessage[]>>;
  readonly activeConversationId: string;
  readonly activeConversationDbId: number | null;
  readonly setActiveConversationDbId: React.Dispatch<React.SetStateAction<number | null>>;
  readonly activeConversationTitle: string;
  readonly draftText: string;
  readonly setDraftText: React.Dispatch<React.SetStateAction<string>>;
  readonly difyConversationId: string | undefined;
  readonly dbConversations: readonly AiConversationRecord[];
  readonly unreadConversationIds: ReadonlySet<number>;
  readonly drawerOpen: boolean;
  readonly setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  readonly notificationRefreshRef: React.MutableRefObject<(() => Promise<void>) | null>;
  readonly resetConversation: () => string;
  /** ★ 发送消息，可附带一次性额外参数 */
  readonly sendMessage: (text: string, overrideInputs?: Readonly<Record<string, unknown>>) => Promise<void>;
  readonly selectConversation: (id: string, title: string) => Promise<void>;
  readonly loadUnreadConversationIds: () => Promise<void>;
  readonly setActiveConversationTitle: React.Dispatch<React.SetStateAction<string>>;
  readonly setDbConversations: React.Dispatch<React.SetStateAction<readonly AiConversationRecord[]>>;
  readonly setUnreadConversationIds: React.Dispatch<React.SetStateAction<ReadonlySet<number>>>;
  /** 对话消息加载中（切换历史对话时为 true） */
  readonly conversationLoading: boolean;
  /** 历史列表是否还有更多数据可加载 */
  readonly historyHasMore: boolean;
  /** 历史列表是否正在加载更多 */
  readonly historyLoadingMore: boolean;
  /** 加载下一页历史会话 */
  readonly loadMoreConversations: () => Promise<void>;
  /** 各会话最新一条消息的创建时间（用于正确排序，避免 date_updated 被 is_active 污染） */
  readonly lastMessageTimes: Readonly<Record<number, string>>;
}

// ─── Hook ─────────────────────

export const useConversationManager = (
  params: UseConversationManagerParams
): UseConversationManagerReturn => {
  const {
    businessType,
    businessId,
    currentCapability,
    currentCapabilityId,
    skillsEnabled,
    selectedSkillIds,
    selectedPattern,
    streaming,
    streamingMsgIdRef,
    scrollToBottom,
    onMessageReceived,
    onBeforeSend,
    previewLastHtmlMessage,
    extraInputs,
  } = params;

  // ─── 状态 ─────────────────────

  const [viewMode, setViewMode] = useState<ViewMode>('home');
  const [draftText, setDraftText] = useState<string>('');
  const [activeConversationId, setActiveConversationId] = useState<string>(
    () => `conv_${createId()}`
  );
  const [activeConversationDbId, setActiveConversationDbId] = useState<number | null>(null);
  const [activeConversationTitle, setActiveConversationTitle] = useState<string>('新对话');
  const [messages, setMessages] = useState<readonly ChatMessage[]>([]);
  const [difyConversationId, setDifyConversationId] = useState<string | undefined>(undefined);
  const [dbConversations, setDbConversations] = useState<readonly AiConversationRecord[]>([]);
  const [unreadConversationIds, setUnreadConversationIds] = useState<ReadonlySet<number>>(
    new Set()
  );
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [conversationLoading, setConversationLoading] = useState<boolean>(false);
  const [historyHasMore, setHistoryHasMore] = useState<boolean>(true);
  const [historyLoadingMore, setHistoryLoadingMore] = useState<boolean>(false);
  /** 各会话最新消息时间：Record<conversationId, ISO string> */
  const [lastMessageTimes, setLastMessageTimes] = useState<Readonly<Record<number, string>>>({});

  // ─── Refs ─────────────────────

  const latestConversationIdRef = useRef<string>(activeConversationId);
  const inflightRequestRef = useRef<{ requestId: string } | null>(null);
  const sseCloseRef = useRef<(() => void) | null>(null);
  const notificationRefreshRef = useRef<(() => Promise<void>) | null>(null);
  const currentStreamingTaskIdRef = useRef<number | null>(null);
  /** 同步防重锁：用 ref 而不是 React state，避免 IntersectionObserver 回调中拿到过期闭包 */
  const historyLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    latestConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  // ─── 清理 ─────────────────────

  const cleanupStreaming = useCallback((): void => {
    sseCloseRef.current?.();
    sseCloseRef.current = null;
    inflightRequestRef.current = null;
    currentStreamingTaskIdRef.current = null;
    streamingMsgIdRef.current = null;
    streaming.reset();
  }, [streaming, streamingMsgIdRef]);

  // ─── DB 会话 ─────────────────────

  const ensureDbConversation = useCallback(async (): Promise<number> => {
    if (activeConversationDbId != null) return activeConversationDbId;
    if (!currentCapabilityId) throw new Error('尚未选择 Dify 能力，无法创建会话');

    try {
      const created = await conversationHistoryService.createConversation({
        businessType,
        difyCapabilityId: currentCapabilityId,
        status: 'active'
      });
      setActiveConversationDbId(created.id);
      // 直接 prepend 到列表头部，不依赖 effect 重新拉取（避免重复和竞态）
      setDbConversations(prev => [created, ...prev]);
      // 将其标记为激活
      void conversationHistoryService.setActiveConversation(created.id, businessType);
      return created.id;
    } catch (error) {
      console.error('[useConversationManager.ensureDbConversation] failed', {
        businessType,
        currentCapabilityId,
        error
      });
      throw error;
    }
  }, [activeConversationDbId, businessType, currentCapabilityId]);

  // ─── 重置会话 ─────────────────────

  const resetConversation = useCallback((): string => {
    cleanupStreaming();
    const newConvId = `conv_${createId()}`;
    setActiveConversationId(newConvId);
    setActiveConversationDbId(null);
    setMessages([]);
    setDifyConversationId(undefined);
    setDraftText('');
    setViewMode('home');
    // 新建对话时清除激活标记，下次进入显示欢迎页
    void conversationHistoryService.clearActiveConversation(businessType);
    return newConvId;
  }, [cleanupStreaming, businessType]);

  // ─── 标记已读工具 ─────────────────────

  const markAsRead = useCallback(async (convIdNum: number): Promise<void> => {
    try {
      await backendChatService.markConversationAsRead(convIdNum);
      setUnreadConversationIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(convIdNum);
        return newSet;
      });
      void notificationRefreshRef.current?.();
    } catch (error) {
      console.error('[useConversationManager] 标记已读失败:', error);
    }
  }, []);

  // ─── 创建流式回调守卫 ─────────────────────

  const createGuardedCallbacks = useCallback((
    conversationId: string,
    requestId: string
  ) => {
    const callbacks = streaming.createCallbacks();

    const isStale = (): boolean =>
      latestConversationIdRef.current !== conversationId ||
      inflightRequestRef.current?.requestId !== requestId;

    return {
      onToken: (delta: string, fullContent: string) => {
        if (isStale()) return;
        callbacks.onToken(delta, fullContent);
      },
      onThinking: (delta: string, thinkingId: string, tool?: string) => {
        if (isStale()) return;
        callbacks.onThinking(delta, thinkingId, tool);
      },
      onFile: (file: any) => {
        if (isStale()) return;
        callbacks.onFile(file);
      },
      onWorkflowTrace: (trace: any) => {
        if (isStale()) return;
        callbacks.onWorkflowTrace(trace);
      },
      onDone: (data: any) => {
        if (isStale()) return;
        callbacks.onDone(data);
      },
      onError: (error: Error) => {
        console.error('[useConversationManager] 流式响应错误:', error);
        callbacks.onError(error);
      }
    };
  }, [streaming]);

  // ─── 订阅流式任务 ─────────────────────

  const subscribeToStreamingTask = useCallback((
    taskId: number,
    thinkingMsgId: string,
    conversationId: string,
    requestId: string
  ): void => {
    streamingMsgIdRef.current = thinkingMsgId;
    streaming.start();

    const guarded = createGuardedCallbacks(conversationId, requestId);
    const close = backendChatService.subscribeToTask({
      taskId,
      ...guarded
    });
    sseCloseRef.current = close;
  }, [streaming, streamingMsgIdRef, createGuardedCallbacks]);

  // ─── 加载会话 + 检查流式任务 ─────────────────────

  const loadConversationWithStreamingCheck = useCallback(async (
    convIdNum: number,
    title: string
  ): Promise<void> => {
    const isUnread = unreadConversationIds.has(convIdNum);

    // 1. 加载消息
    const msgRows = await conversationHistoryService.getMessages({
      conversationId: convIdNum,
      limit: 200
    });
    const uiMessages = mapDbMessagesToUi(msgRows);
    setMessages(uiMessages);

    // 2. 检查进行中的流式任务
    try {
      const pendingTask = await backendChatService.getPendingTaskByConversation(convIdNum);

      if (pendingTask && (pendingTask.status === 'pending' || pendingTask.status === 'streaming')) {
        const thinkingMsgId = `dbmsg_${pendingTask.messageId}`;
        const requestId = `req_${createId()}`;
        const conversationId = `conv_${createId()}`;

        inflightRequestRef.current = { requestId };
        setActiveConversationId(conversationId);
        latestConversationIdRef.current = conversationId;
        currentStreamingTaskIdRef.current = pendingTask.taskId;

        setMessages(prev =>
          prev.map(m =>
            m.id === thinkingMsgId
              ? { ...m, content: pendingTask.content, status: 'thinking' as const }
              : m
          )
        );

        subscribeToStreamingTask(pendingTask.taskId, thinkingMsgId, conversationId, requestId);
        await markAsRead(convIdNum);
        return;
      }
    } catch (error) {
      console.error('[useConversationManager] 检查流式任务失败:', error);
    }

    // 3. 标记已读
    await markAsRead(convIdNum);

    // 4. 未读会话自动预览
    if (isUnread && uiMessages.length > 0) {
      setTimeout(() => previewLastHtmlMessage(uiMessages), 100);
    }

    // ★ 5. 从历史消息中提取最后一条含 workflowTrace.outputs?.table_id 的消息，通知父组件
    if (onMessageReceived && uiMessages.length > 0) {
      // 从后往前找第一条含 workflowTrace 的 assistant 消息
      for (let i = uiMessages.length - 1; i >= 0; i--) {
        const msg = uiMessages[i];
        if (msg.role === 'assistant' && msg.workflowTrace && msg.workflowTrace.outputs?.table_id) {
          onMessageReceived(msg.content, {
            workflowTrace: msg.workflowTrace,
          });
          break;
        }
      }
    }
  }, [subscribeToStreamingTask, unreadConversationIds, markAsRead, previewLastHtmlMessage, onMessageReceived]);

  // ─── 发送消息 + 启动流式 ─────────────────────

  const startDifyAssistantTask = useCallback(async (taskParams: {
    readonly conversationId: string;
    readonly userText: string;
    readonly dbConversationId: number;
    /** ★ 一次性额外参数，合并到本次请求的 inputs 中 */
    readonly overrideInputs?: Readonly<Record<string, unknown>>;
  }): Promise<void> => {
    const { conversationId, userText, dbConversationId, overrideInputs } = taskParams;

    if (!currentCapability) {
      setMessages(prev => [
        ...prev,
        {
          id: `msg_${createId()}`,
          role: 'assistant' as const,
          content: '当前没有可用的 Dify 能力，请先配置并启用一个能力。',
          dateCreated: nowIso(),
          status: 'error' as const
        }
      ]);
      return;
    }

    const thinkingMsgId = `msg_${createId()}`;
    const requestId = `req_${createId()}`;
    inflightRequestRef.current = { requestId };

    // 添加 thinking 占位
    setMessages(prev => [
      ...prev,
      {
        id: thinkingMsgId,
        role: 'assistant' as const,
        content: '',
        dateCreated: nowIso(),
        status: 'thinking' as const
      }
    ]);

    streamingMsgIdRef.current = thinkingMsgId;
    streaming.reset();
    streaming.start();
    scrollToBottom(true);

    try {
      sseCloseRef.current?.();

      const extra: Record<string, unknown> = {};
      if (skillsEnabled && selectedSkillIds.length > 0) {
        extra.skills = formatSkillsParam(selectedSkillIds);
      }
      if (selectedPattern) {
        extra.pattern = PATTERN_FEATURE_MAP[selectedPattern];
      }

      // ★ 合并外部传入的额外输入参数
      if (extraInputs && Object.keys(extraInputs).length > 0) {
        Object.assign(extra, extraInputs);
      }

      // ★ 合并一次性的 overrideInputs（优先级最高，覆盖 extraInputs 中的同名字段）
      if (overrideInputs && Object.keys(overrideInputs).length > 0) {
        Object.assign(extra, overrideInputs);
      }

      const guarded = createGuardedCallbacks(conversationId, requestId);

      const userId = sessionStorage.getItem('agimx_user_id') ?? undefined;

      const { close, conversationId: newDbConvId } = await backendChatService.sendAndStream({
        request: {
          conversationId: dbConversationId,
          difyCapabilityId: currentCapability.id,
          userInput: userText,
          userId,
          extra
        },
        ...guarded
      });

      sseCloseRef.current = close;

      if (newDbConvId && newDbConvId !== dbConversationId) {
        setActiveConversationDbId(newDbConvId);
      }
    } catch (error: unknown) {
      console.error('[useConversationManager] 发送失败:', error);
      const errorMessage = error instanceof Error ? error.message : '发送失败';
      setMessages(prev =>
        prev.map(m =>
          m.id === thinkingMsgId
            ? { ...m, content: errorMessage, status: 'error' as const, dateCreated: nowIso() }
            : m
        )
      );
      cleanupStreaming();
    }
  }, [
    currentCapability, scrollToBottom, createGuardedCallbacks,
    streaming, streamingMsgIdRef, cleanupStreaming,
    skillsEnabled, selectedSkillIds, selectedPattern,
    extraInputs,
  ]);

  const sendMessage = useCallback(async (
    text: string,
    /** ★ 一次性额外参数，仅对本次请求生效 */
    overrideInputs?: Readonly<Record<string, unknown>>
  ): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const finalText = onBeforeSend ? onBeforeSend(trimmed) : trimmed;

    try {
      const dbConversationId = await ensureDbConversation();

      // 乙观更新：发消息后将当前对话置顶，反映最新消息时间
      setDbConversations(prev => {
        const idx = prev.findIndex(c => c.id === dbConversationId);
        if (idx <= 0) return prev;
        const target = prev[idx];
        return [target, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
      });

      // 乙观更新 lastMessageTimes
      setLastMessageTimes(prev => ({ ...prev, [dbConversationId]: new Date().toISOString() }));

      const userMsg: ChatMessage = {
        id: `pending_user_${createId()}`,
        role: 'user',
        content: finalText,
        dateCreated: nowIso(),
        status: 'done'
      };

      setMessages(prev => [...prev, userMsg]);
      setDraftText('');

      void startDifyAssistantTask({
        conversationId: activeConversationId,
        userText: finalText,
        dbConversationId,
        overrideInputs,
      });
    } catch (error: any) {
      console.error('[useConversationManager.sendMessage] failed', {
        businessType,
        businessId,
        error
      });
      setMessages(prev => [
        ...prev,
        {
          id: `msg_${createId()}`,
          role: 'assistant' as const,
          content: error?.message ?? '发送失败，请稍后重试。',
          dateCreated: nowIso(),
          status: 'error' as const
        }
      ]);
    }
  }, [
    activeConversationId, businessId, businessType,
    ensureDbConversation, startDifyAssistantTask, onBeforeSend
  ]);

  // ─── 选择会话 ─────────────────────

  const selectConversation = useCallback(async (
    id: string,
    title: string
  ): Promise<void> => {
    const convIdNum = Number(id);
    if (!Number.isFinite(convIdNum)) return;

    cleanupStreaming();

    setMessages([]);
    setActiveConversationTitle(title);
    setActiveConversationDbId(convIdNum);
    setActiveConversationId(`conv_${createId()}`);
    setViewMode('chat');
    setDifyConversationId(undefined);
    setConversationLoading(true);

    // 切换对话时更新激活标记
    void conversationHistoryService.setActiveConversation(convIdNum, businessType);

    try {
      await loadConversationWithStreamingCheck(convIdNum, title);
    } catch (error) {
      console.error('[useConversationManager] select conversation failed', { id, error });
    } finally {
      setConversationLoading(false);
    }
  }, [cleanupStreaming, loadConversationWithStreamingCheck, businessType]);

  // ─── 未读管理 ─────────────────────

  const loadUnreadConversationIds = useCallback(async (): Promise<void> => {
    try {
      const unreadIds = await backendChatService.getUnreadConversationIds(businessType);
      setUnreadConversationIds(new Set(unreadIds));
    } catch (error) {
      console.error('[useConversationManager] 加载未读会话失败:', error);
    }
  }, [businessType]);

  // ─── 会话列表加载（首页，重置分页） ─────────────────────

  useEffect(() => {
    let mounted = true;
    const run = async (): Promise<void> => {
      try {
        const list = await conversationHistoryService.listConversations({
          businessType,
          limit: 20,
          offset: 0,
        });
        if (!mounted) return;
        setDbConversations(list);
        setHistoryHasMore(list.length === 20);
      } catch (error) {
        console.error('[useConversationManager] load conversations failed', {
          businessType,
          businessId,
          error
        });
      }
    };
    void run();
    return () => { mounted = false; };
  // 移除 activeConversationDbId：切换对话不重新拉取列表（避免覆盖 loadMore 的结果并射入重复）
  }, [businessId, businessType]);

  // ─── 独立查询最新消息时间（不干扰列表加载逻辑） ─────────────────────
  // 监听 dbConversations 变化，批量查各会话的最新消息时间用于排序
  // 与列表加载完全解耦，不会引入竞态条件

  useEffect(() => {
    if (dbConversations.length === 0) return;
    let mounted = true;
    const ids = dbConversations.map(c => c.id);
    const run = async (): Promise<void> => {
      const times = await conversationHistoryService.getLatestMessageTimes(ids);
      if (mounted) setLastMessageTimes(prev => ({ ...prev, ...times }));
    };
    void run();
    return () => { mounted = false; };
  }, [dbConversations]);

  // ─── 加载更多历史会话 ─────────────────────

  const loadMoreConversations = useCallback(async (): Promise<void> => {
    // 用 ref 做同步防重锁，而不是 React state，避免 IntersectionObserver 回调中读到过期闭包
    if (historyLoadingRef.current || !historyHasMore) return;
    historyLoadingRef.current = true;
    setHistoryLoadingMore(true);
    try {
      const list = await conversationHistoryService.listConversations({
        businessType,
        limit: 20,
        offset: dbConversations.length,
      });
      // id 去重：防止分页窗口偏移导致重叠
      setDbConversations(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const toAdd = list.filter(c => !existingIds.has(c.id));
        return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
      });
      setHistoryHasMore(list.length === 20);
    } catch (error) {
      console.error('[useConversationManager] loadMoreConversations failed', error);
      // 出错（如无权限）时终止继续加载，避免无限重试
      setHistoryHasMore(false);
    } finally {
      historyLoadingRef.current = false;
      setHistoryLoadingMore(false);
    }
  }, [historyHasMore, businessType, dbConversations.length]);

  // ─── 标题同步 ─────────────────────

  useEffect(() => {
    if (activeConversationDbId && dbConversations.length > 0) {
      const found = dbConversations.find(c => c.id === activeConversationDbId);
      if (found?.title) setActiveConversationTitle(found.title);
    }
  }, [activeConversationDbId, dbConversations]);

  // ─── 滚动触发 ─────────────────────

  useEffect(() => {
    if (messages.length > 0) scrollToBottom(true);
  }, [messages.length, scrollToBottom]);

  // ─── 组件卸载清理 ─────────────────────

  useEffect(() => {
    return () => {
      sseCloseRef.current?.();
      inflightRequestRef.current = null;
    };
  }, []);

  return {
    viewMode,
    setViewMode,
    messages,
    setMessages,
    activeConversationId,
    activeConversationDbId,
    setActiveConversationDbId,
    activeConversationTitle,
    draftText,
    setDraftText,
    difyConversationId,
    dbConversations,
    unreadConversationIds,
    drawerOpen,
    setDrawerOpen,
    notificationRefreshRef,
    resetConversation,
    sendMessage,
    selectConversation,
    loadUnreadConversationIds,
    setActiveConversationTitle,
    setDbConversations,
    setUnreadConversationIds,
    conversationLoading,
    historyHasMore,
    historyLoadingMore,
    loadMoreConversations,
    lastMessageTimes,
  };
};